// @ts-check
import React from 'react';
import { injectIntl } from 'react-intl';

import BusComponent from '../BusComponent';
import Tooltip from '../Tooltip';
import { isEqualJson, mapLocationAnalysisItemToVisualReport } from '../../helpers/Util';
import Mixpanel, { MIXPANEL_EVENTS } from '../../helpers/Mixpanel';

/**
 * @typedef Props
 * @property {import('../../types').VisualReport} visualReport
 * @property {import('react-intl').intlShape} intl
 *
 * @typedef State
 * @property {boolean} saved
 * @property {import('../../types').VisualReport} visualReport
 * @property {import('../../types').VisualReport[]} param0.savedReports
 *
 * @extends {BusComponent<Props, State>}
 */
class VisualReportSave extends BusComponent {
    constructor(props, context) {
        super(props, context);
        /** @type {State} */
        this.state = {
            saved: false,
            visualReport: props.visualReport,
            savedReports: [],
        };
    }

    componentDidMount() {
        this.bindGluBusEvents({
            VISUAL_REPORT_SAVE_SUCCESS: this.onSaveSuccess,
            VISUAL_REPORT_UPDATE_SUCCESS: this.onUpdateSuccess,
            VISUAL_REPORT_DELETE_SUCCESS: this.onDeleteSuccess,
            LOCATION_ANALYSIS_ITEM_UPDATED: this.onReset,
            VISUAL_REPORTS: this.onRetrievedVisualReports,
            POPUP_STATE_CHANGED: this.onPopupStateChanged,
        });
        this.emit('VISUAL_REPORTS_GET_REQUEST', { source: this });
    }

    componentWillUnmount() {
        this.unbindGluBusEvents();
    }

    onRetrievedVisualReports = payload => {
        const savedReports = payload.visualReports;
        this.setState({ savedReports }, () => {
            this.checkIfAlreadySaved(this.props.visualReport, savedReports);
        });
    };

    /**
     * @param {import('../../types').VisualReport} visualReport
     * @param {import('../../types').VisualReport[]} savedReports
     */
    checkIfAlreadySaved = (visualReport, savedReports) => {
        if (visualReport.id) {
            this.setState({ saved: true });
            if (!visualReport.listId) {
                const saved = savedReports.find(r => r.id === visualReport.id);
                if (saved) {
                    this.setState({ visualReport: { ...visualReport, listId: saved.listId } });
                }
            }

            return;
        }
        let saved = null;
        const selection = [...visualReport.selection].sort();
        if (visualReport.facilityId) {
            saved = savedReports.find(
                report =>
                    report.facilityId === visualReport.facilityId &&
                    isEqualJson([...report.selection].sort(), selection) &&
                    report.profile === visualReport.profile &&
                    (report.profile !== 'polygon' || report.polygon === visualReport.polygon),
            );
        } else {
            saved = savedReports.find(
                report =>
                    isEqualJson(report.point, visualReport.point) &&
                    isEqualJson([...report.selection].sort(), selection) &&
                    report.profile === visualReport.profile &&
                    (report.profile !== 'polygon' || report.polygon === visualReport.polygon),
            );
        }

        if (saved) {
            this.setState({
                saved: true,
                visualReport: {
                    ...this.state.visualReport,
                    id: saved.id,
                    listId: saved.listId,
                    status: saved.status,
                    notes: saved.notes,
                },
            });
        } else {
            this.setState({ saved: false });
        }
    };

    onSaveSuccess = ({ newReport }) => {
        this.setState({
            saved: true,
            visualReport: newReport,
        });
    };

    onUpdateSuccess = ({ newReport }) => {
        this.setState({
            saved: true,
            visualReport: newReport,
        });
    };

    onDeleteSuccess = ({ reportId }) => {
        const { visualReport } = this.state;
        if (visualReport.id === reportId) {
            this.setState({ saved: false, visualReport: { ...visualReport, id: undefined } });
        }
    };

    onSave = () => {
        const { saved, visualReport } = this.state;

        if (saved) {
            Mixpanel.emitUserEvent(MIXPANEL_EVENTS.EDIT_DEAL_FROM_INSIGHTS, {
                timestamp: new Date().toISOString(),
            });
        }

        this.emit('OPEN_EDIT_MARKET_POPUP_REQUEST', {
            visualReport,
        });
    };

    onPopupStateChanged = ({ isOpen }) => {
        this.setState({ popupOpen: isOpen });
    };

    onReset = ({ locationAnalysisItem }) => {
        const newVR = mapLocationAnalysisItemToVisualReport(locationAnalysisItem);

        if (this.state.popupOpen) {
            this.bus.emit('UPDATE_POPUP_SELECTION', {
                selection: newVR.selection,
                profile: newVR.profile,
            });
            return;
        }
        const idChanged = this.state.visualReport.id !== newVR.id;

        const oldPoint = this.state.visualReport.point;
        const newPoint = newVR.point;
        // If the pin didn't move (same lat/lng), just merge in the new selection/profile
        if (
            !idChanged &&
            oldPoint &&
            newPoint &&
            oldPoint.lat === newPoint.lat &&
            oldPoint.lng === newPoint.lng
        ) {
            this.setState(({ visualReport }) => ({
                visualReport: {
                    ...visualReport,
                    selection: newVR.selection,
                    profile: newVR.profile,
                    polygon: newVR.polygon,
                    title: newVR.title || visualReport.title,
                },
            }));
        } else {
            // Location moved: reset saved, clear id, then re-check against savedReports
            this.setState({ visualReport: newVR, saved: false }, () =>
                this.checkIfAlreadySaved(newVR, this.state.savedReports),
            );
        }
    };

    render() {
        const { intl } = this.props;
        const { saved } = this.state;

        const icon = saved ? 'edit' : 'add_location_alt';
        const tooltipMessageId = saved ? 'locationAnalysis.editDeal' : 'locationAnalysis.saveDeal';

        const label = intl.formatMessage({
            id: saved ? 'locationAnalysis.edit' : 'locationAnalysis.save',
        });

        return (
            <Tooltip
                placement="bottom"
                mouseEnterDelay={0.5}
                mouseLeaveDelay={0}
                overlay={intl.formatMessage({
                    id: tooltipMessageId,
                })}
            >
                <button
                    className="btn-chip btn-chip--insight-panel"
                    onClick={this.onSave}
                    data-testid="deal-save-chip"
                >
                    <i className="material-symbols-filled btn-chip__icon"> {icon}</i>
                    <span>{label}</span>
                </button>
            </Tooltip>
        );
    }
}

export default injectIntl(VisualReportSave);
